import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../Helpers/Handler";
import { disconnectSocket } from "../Helpers/socketClient";
import { unregisterThisDevice } from "../Helpers/pushDevice";
import { deleteAllCookies, getCookie, reloadWindowToPath, setCookie, storageRemoveItem } from "../Helpers/Utils";
import { beginSignOut, markSignedOut, startedSignIn } from "../Helpers/webSession";

const AuthContext = createContext();

function AuthenticateProvider({ children }) {
    const token = getCookie("token");
    const [isAuthenticated, setIsAuthenticated] = useState(!!token);
    const [siteDetails, setSiteDetails] = useState({});
    const [profile, setProfile] = useState({});
    const [profileLoading, setProfileLoading] = useState(!!token);

    // session = when this sign-in ends, from the sign-in answer (see Helpers/webSession).
    const logIn = (authToken, userId, session) => {
        setCookie("token", authToken);
        setCookie("userId", userId);
        startedSignIn(session);
        storageRemoveItem("filter");
        setIsAuthenticated(true);
    };

    // Ends this browser's sign-in only; the same person's phone and other browsers stay signed in.
    // options.returnToPage: after the next sign-in come back to this page (used by "Sign in again now").
    const logOut = async (options = {}) => {
        beginSignOut({ returnToPage: options?.returnToPage === true });
        // Stop this browser receiving the staff member's notifications before the session ends.
        await unregisterThisDevice();
        // silent: if the sign-in is already over the server answers 401, and that must not bounce us around.
        await Handler({ method: "post", url: "/staff/session/logout", data: {}, silent: true });
        markSignedOut(); // tells this browser's other tabs to leave too
        toast.success("Logout successfully");
        disconnectSocket();
        deleteAllCookies();
        storageRemoveItem("filter");
        setIsAuthenticated(false);
        reloadWindowToPath("/");
    };

    const getSiteDetails = useCallback(async () => {
        const response = await Handler({ method: "GET", url: "/staff/business/view/site-details" });
        setSiteDetails(response?.success ? response?.data?.data || {} : {});
    }, []);

    const getProfile = useCallback(async () => {
        const response = await Handler({ method: "GET", url: "/staff/me" });
        setProfile(response?.success ? response?.data?.data || {} : {});
        setProfileLoading(false);
    }, []);

    useEffect(() => {
        if (token) setIsAuthenticated(true);
    }, [token]);

    useEffect(() => {
        if (!isAuthenticated) return;
        setProfileLoading(true);
        getSiteDetails();
        getProfile();
    }, [isAuthenticated, getSiteDetails, getProfile]);

    const hasPermission = useCallback(
        (capabilityKey) => Array.isArray(profile?.permissions) && profile.permissions.includes(capabilityKey),
        [profile]
    );

    return (
        <AuthContext.Provider value={{ logIn, logOut, isAuthenticated, siteDetails, profile, profileLoading, getSiteDetails, getProfile, hasPermission }}>
            {children}
        </AuthContext.Provider>
    );
}

export default AuthenticateProvider;

export const useAuth = () => useContext(AuthContext);
